"use client"

import { useState, useEffect } from "react"
import { getverifyrequest, verifyStudentRequest } from "../comman/Student.js"
import Sidebar from "../components/Sidebar.js"
import { CheckCircleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid"
import { ExclamationCircleIcon } from "@heroicons/react/24/outline"

const VerificationRequests = () => {
  const [verificationRequests, setVerificationRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const data = await getverifyrequest()
      setVerificationRequests(data.students)
      setLoading(false)
    } catch (error) {
      setError("Error fetching verification requests. Please try again.")
      setLoading(false)
    }
  }

  const handleVerify = async (studentId, requestId) => {
    try {
      await verifyStudentRequest(studentId, requestId)
      fetchData()
    } catch (error) {
      console.error("Error verifying student:", error)
      setError("Error verifying student. Please try again.")
    }
  }

  const filteredRequests = verificationRequests
    .flatMap((student) =>
      student.batchDetails.flatMap((batch) =>
        batch.requests.map((request) => ({
          ...request,
          studentName: student.name || "",
          studentEmail: student.email || "",
          studentId: student._id,
        })),
      ),
    )
    .filter(
      (request) =>
        (request.studentName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.studentEmail || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.requestedUser?.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.requestedUser?.email || "").toLowerCase().includes(searchQuery.toLowerCase()),
    )

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Verification Requests</h1>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <ExclamationCircleIcon className="h-5 w-5 inline mr-2" />
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 md:-mx-6">
                <div className="inline-block min-w-full align-middle p-4 md:p-6">
                  <div className="overflow-hidden border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-[#12143F]">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Email
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Requested By
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredRequests.map((request) => (
                          <tr key={request._id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                              {request.studentName}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                              {request.studentEmail}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900">
                              {request.requestedUser ? (
                                <div>
                                  <p className="font-semibold">{request.requestedUser.name}</p>
                                  <p className="text-gray-500 text-xs">{request.requestedUser.email}</p>
                                </div>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              {!request.verify ? (
                                <button
                                  onClick={() => handleVerify(request.studentId, request._id)}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150"
                                >
                                  Verify
                                </button>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircleIcon className="mr-1 h-4 w-4" />
                                  Verified
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerificationRequests

